import { DollarSign } from "lucide-react";
import { useMemo } from "react";
import { useSubscriptionDisplay } from "@/hooks/useSubscriptionDisplay";
import { type Subscription } from "@/types/subscription";

interface SubscriptionAmountProps {
  subscription: Subscription;
}

const parsePaymentDate = (p: any) => {
  if (!p) return null;
  if (p.date) return new Date(p.date);
  if (p.payment_date) return new Date(p.payment_date);
  if (p.paymentDate) return new Date(p.paymentDate);
  return null;
};

export const SubscriptionAmount = ({
  subscription,
}: SubscriptionAmountProps) => {
  // For recurring subscriptions keep existing formatted amount
  const { formatAmount, getFrequencyText } =
    useSubscriptionDisplay(subscription);
  const isScheduledPAYG =
    subscription.billingType === "pay_as_you_go" && !!subscription.frequency;

  // PAYG: compute recent totals and average monthly from paymentHistory if available
  const paygStats = useMemo(() => {
    if (subscription.billingType !== "pay_as_you_go") return null;
    const now = new Date();
    const msInDay = 24 * 60 * 60 * 1000;
    const payments = subscription.paymentHistory || [];
    // Normalize payment entries and filter to same currency as subscription where possible
    const normalized = payments
      .map((p: any) => {
        const date =
          parsePaymentDate(p) || (p.createdAt ? new Date(p.createdAt) : null);
        return {
          amount: p.amount ?? p.settlement_amount ?? 0,
          currency: p.currency ?? subscription.currency,
          date,
        };
      })
      .filter((p: any) => p.date);

    // Total in last 30 days
    const cutoff30 = new Date(now.getTime() - 30 * msInDay);
    const total30 = normalized
      .filter((p: any) => p.date >= cutoff30)
      .reduce((s: number, p: any) => s + (p.amount || 0), 0);

    // Average monthly over last 90 days (3 months)
    const cutoff90 = new Date(now.getTime() - 90 * msInDay);
    const total90 = normalized
      .filter((p: any) => p.date >= cutoff90)
      .reduce((s: number, p: any) => s + (p.amount || 0), 0);
    const avgMonthly = total90 / 3;

    // Use subscription.currency for display formatting
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: subscription.currency,
    });

    return {
      total30,
      avgMonthly,
      formattedTotal30: formatter.format(total30),
      formattedAvgMonthly: formatter.format(avgMonthly),
      hasPayments: normalized.length > 0,
    };
  }, [subscription]);

  return (
    <div className="mb-4 flex items-center justify-between">
      <div className="flex flex-col">
        <div className="flex items-center space-x-2">
          <DollarSign className="h-4 w-4 text-muted-foreground" />
          <span className="amount-text text-xl font-bold text-card-foreground">
            {isScheduledPAYG
              ? "Amount TBD"
              : subscription.billingType === "pay_as_you_go"
                ? (paygStats?.formattedAvgMonthly ?? "—")
                : formatAmount()}
          </span>
          <span className="text-sm text-muted-foreground">
            {isScheduledPAYG
              ? getFrequencyText()
              : subscription.billingType === "pay_as_you_go"
                ? "/month (avg)"
                : getFrequencyText()}
          </span>
        </div>
        {subscription.billingType === "pay_as_you_go" && (
          <div className="mt-1 text-xs text-muted-foreground">
            {paygStats?.hasPayments
              ? `Total last 30 days: ${paygStats.formattedTotal30}`
              : "No recorded PAYG payments yet"}
          </div>
        )}
      </div>

      {subscription.paymentMethod && (
        <span className="rounded bg-muted px-2 py-1 text-xs text-muted-foreground">
          {subscription.paymentMethod}
        </span>
      )}
    </div>
  );
};
